/* eslint‑disable react/prop‑types */
import React, { useState, useMemo, useEffect } from 'react';
import {
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    TablePagination,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../../Utils/FormatNumber';

/* ───────────── helpers ───────────── */
const monthYear = (d) =>
    `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

export const formatDateRange = (from, to) =>
    `${monthYear(new Date(to))} - ${monthYear(new Date(from))}`;

export const formatYearRange = (rng) => {
    const [y1] = rng.split('-');
    return `${y1.slice(0, 4)}`;
};

const cellFmt = (v) => {
    const formatted = beautifyNumber(v || 0);
    // Check if the formatted number contains parentheses (negative numbers)
    if (formatted.includes('(') && formatted.includes(')')) {
        return <span style={{ color: '#d32f2f' }}>{formatted}</span>;
    }
    return formatted;
};

/* ───────────── component ───────────── */
const ProfitAndLossReportTable = ({
    rows = [],
    columns = [],
    isMonthly,
    startDate,
    endDate,
    footerTotals = {},
    specialNode,
    handleSelect,
    onAssignSortCodeGroup,
}) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();

    /* pagination */
    const [page, setPage] = useState(0);
    const [rpp, setRpp] = useState(50);
    useEffect(() => setPage(0), [rows]);

    const pageRows = useMemo(() => {
        if (!rows.length) return [];
        const s = page * rpp;
        return rows.slice(s, s + rpp);
    }, [rows, page, rpp]);


    /* expansion */
    const [open, setOpen] = useState({});
    const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

    const headStyle = {
        backgroundColor: '#E3E7FE',
        color: '#1222b3',
        fontWeight: 600,
        fontSize: '13px',
        textAlign: 'right',
        padding: 10,
    };

    const cellStyle = {
        textAlign: 'right',
        padding: '8px 4px',
    };

    const expandCellStyle = {
        textAlign: 'center',
        padding: '4px',
        border: '0.1px solid #e0e0e0',
        width: '50px',
        textWeight: 'bold',
        background: '#f5f5f5',
    };

    const descriptionCellStyle = {
        textAlign: 'right',
        padding: '8px 8px',
        marginRight: '5px',
        border: '0.5px solid #e0e0e0',
        fontWeight: 500,
    };

    const footerStyle = {
        background: 'rgb(251 251 251)',
        textAlign: 'right',
        marginLeft: '5px',
        fontWeight: 'bold',
        padding: '8px 4px',
        border: '0.2px solid #e0e0e0',
    };

    const onChildClick = (desc, isSpecial, sortCodeId) =>
        handleSelect && handleSelect(desc, isSpecial, sortCodeId);

    /* Rows backed by a real sort code can be edited from the context menu.
       "-1" marks the bucket of documents without a sort code, which has nothing to edit. */
    const canEditSortCode = (row) =>
        Boolean(onAssignSortCodeGroup && row?.SortCodeId && String(row.SortCodeId) !== '-1');

    /* ───────────── JSX ───────────── */
    return (
        <div className={dir} style={{ height: '65vh', width: '100%' }}>
            <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
                <Table stickyHeader>
                    {/* ① header */}
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ ...expandCellStyle, backgroundColor: '#E3E7FE' }} />
                            <TableCell style={headStyle}>{t('Description')}</TableCell>
                            {columns.map((c) => (
                                <TableCell key={c.field} style={headStyle}>
                                    {isMonthly ? c.header : formatYearRange(c.header)}
                                </TableCell>
                            ))}
                            {isMonthly && (
                                <TableCell style={headStyle}>
                                    {t('PeriodProfit')} {formatDateRange(startDate, endDate)}
                                </TableCell>
                            )}

                        </TableRow>
                    </TableHead>

                    {/* ② body */}
                    <TableBody>
                        {[...pageRows, specialNode].filter(Boolean).map((node) => {
                            /* does this node have at least ONE non‑subtotal child? */
                            const hasRealChildren = node.children?.some((c) => !c.isSubtotal);

                            return (
                                <React.Fragment key={node.key}>
                                    {/* parent */}
                                    <TableRow 
                                        hover
                                        sx={{
                                            backgroundColor: hasRealChildren && open[node.key] ? 'linear-gradient(135deg, rgb(227, 242, 253) 0%, rgb(224, 239, 251) 100%)' : 'inherit',
                                        }}
                                    >
                                        <TableCell style={expandCellStyle}>
                                            {hasRealChildren && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggle(node.key)}
                                                >
                                                    {open[node.key] ? <ExpandLess /> : <ExpandMore />}
                                                </IconButton>
                                            )}
                                        </TableCell>

                                        <TableCell style={descriptionCellStyle}>{node.data.Description}</TableCell>

                                        {columns.map(({ field }) => (
                                            <TableCell key={field} style={cellStyle}>
                                                {cellFmt(node.data[field])}
                                            </TableCell>
                                        ))}

                                        {isMonthly && (
                                            <TableCell style={cellStyle}>{cellFmt(node.data.total)}</TableCell>
                                        )}
                                    </TableRow>

                                    {/* children – regular rows */}
                                    {open[node.key] &&
                                        node.children
                                            ?.filter((ch) => !ch.isSubtotal)
                                            .map((ch) => (
                                                <TableRow
                                                    key={ch.key}
                                                    hover
                                                    onClick={() =>
                                                        onChildClick(
                                                            ch.data.Description,
                                                            node === specialNode,
                                                            ch.data.SortCodeId
                                                        )
                                                    }
                                                    onContextMenu={
                                                        canEditSortCode(ch.data)
                                                            ? (e) => {
                                                                e.preventDefault();
                                                                onAssignSortCodeGroup(ch.data);
                                                            }
                                                            : undefined
                                                    }
                                                    title={
                                                        canEditSortCode(ch.data)
                                                            ? node.data.isWithoutSortCodeGroup
                                                                ? t('RightClickToAssignSortCodeGroup')
                                                                : t('RightClickToEditSortCode')
                                                            : undefined
                                                    }
                                                    sx={{ 
                                                        cursor: 'pointer',
                                                         background: 'linear-gradient(135deg, rgb(227, 242, 253) 0%, rgb(224, 239, 251) 100%)',
                                                    }}
                                                >
                                                    <TableCell style={expandCellStyle} />
                                                    <TableCell style={{ ...descriptionCellStyle, pl: 6 }}>
                                                        {ch.data.Description}
                                                    </TableCell>
                                                    {columns.map(({ field }) => (
                                                        <TableCell key={field} style={cellStyle}>
                                                            {cellFmt(ch.data[field])}
                                                        </TableCell>
                                                    ))}
                                                    {isMonthly && (
                                                        <TableCell style={cellStyle}>{cellFmt(ch.data.total)}</TableCell>
                                                    )}
                                                </TableRow>
                                            ))}

                                    {/* constant subtotal row – always displayed */}
                                    {node.children
                                        ?.filter((ch) => ch.isSubtotal)
                                        .map((ch) => (
                                            <TableRow 
                                                key={ch.key} 
                                                hover
                                                sx={{
                                                    backgroundColor: open[node.key] ? '#f8f9fa' : 'inherit',
                                                }}
                                            >
                                                <TableCell style={expandCellStyle} />
                                                <TableCell
                                                    style={{ ...descriptionCellStyle, pl: 6, fontWeight: 700 }}
                                                >
                                                    {ch.data.Description}
                                                </TableCell>
                                                {columns.map(({ field }) => (
                                                    <TableCell
                                                        key={field}
                                                        style={{ ...cellStyle, fontWeight: 700 }}
                                                    >
                                                        {cellFmt(ch.data[field])}
                                                    </TableCell>
                                                ))}
                                                {isMonthly && (
                                                    <TableCell style={{ ...cellStyle, fontWeight: 700 }}>
                                                        {cellFmt(ch.data.total)}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                </React.Fragment>
                            );
                        })}

                        {/* ③ footer (sticky) */}
                        <TableRow
                            sx={{
                                background: '#e8e8e8',
                                position: 'sticky',
                                bottom: 0,
                            }}
                        >
                            <TableCell style={expandCellStyle} />
                            <TableCell style={footerStyle}>
                                <strong>{t('Total')}</strong>
                            </TableCell>
                            {columns.map(({ field }) => (
                                <TableCell key={field} style={footerStyle}>
                                    <strong>{cellFmt(footerTotals[field])}</strong>
                                </TableCell>
                            ))}
                            {isMonthly && (
                                <TableCell style={footerStyle}>
                                    <strong>{cellFmt(footerTotals.total)}</strong>
                                </TableCell>
                            )}
                        </TableRow>
                    </TableBody>
                </Table>

                {/* ④ pagination */}
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[25, 50, 100]}
                    count={rows.length}
                    rowsPerPage={rpp}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                        setRpp(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    labelRowsPerPage={t('Rows per page')}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${t('of')} ${count !== -1 ? count : `> ${to}`}`
                    }
                />
            </TableContainer>
        </div>
    );
};

export default ProfitAndLossReportTable;
